import { Check, ChevronsUpDown, Database } from 'lucide-react'
import { useState } from 'react'
import { Link, useLocation, useParams } from 'react-router'
import { BackupStatusIcon } from '@/components/sv/backup-status-icon'
import { DatabaseStatusIcon } from '@/components/sv/database-status-icon'
import { SidebarMenuButton } from '@/components/sv/sidebar'
import { BreadcrumbItem } from '@/components/ui/breadcrumb'
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { gql } from '@/lib/graphql/gql'
import { useSuspenseQuery, useIdCompany } from '@/lib/hooks'
import { cn } from '@/lib/utils'
import type {
  DatabaseSwitcherQuery,
  DatabaseSwitcherQueryVariables,
} from '@/components/layout/breadcrumbs/database/database-switcher/switcher.types.gen'
import type { BackupStatus, DatabaseStatus } from '@/graphql.types'

const DATABASE_SWITCHER_QUERY = gql`
  query DatabaseSwitcher($idCompany: String!) {
    company(id: $idCompany) {
      id
      databases {
        id
        total
        items {
          id
          name
          status
          backupStatus
          isSuspended
          displayName
        }
      }
    }
  }
`

export const DatabaseSwitcherData = () => {
  const { pathname } = useLocation()
  const [open, setOpen] = useState(false)
  const { databaseName = '' } = useParams()
  const { idCompany } = useIdCompany()

  const { data } = useSuspenseQuery<
    DatabaseSwitcherQuery,
    DatabaseSwitcherQueryVariables
  >(DATABASE_SWITCHER_QUERY, {
    variables: { idCompany },
  })

  const databases = data?.company?.databases?.items ?? []

  const activeDatabase =
    databases.find((db) => db.name === databaseName) || databases[0]

  const isSuspended = activeDatabase?.isSuspended
  const isBackupUpdating =
    activeDatabase?.backupStatus === 'creating' ||
    activeDatabase?.backupStatus === 'deleting'
  const status: DatabaseStatus | BackupStatus | 'suspended' | undefined | null =
    isSuspended ? 'suspended' : activeDatabase?.status

  return (
    <BreadcrumbItem>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <SidebarMenuButton
            size="lg"
            aria-expanded={open}
            className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
          >
            <div className="grid flex-1 text-left text-sm leading-tight">
              <div className="flex items-center gap-2">
                {isBackupUpdating ? (
                  <BackupStatusIcon
                    status={activeDatabase?.backupStatus as BackupStatus}
                  />
                ) : status ? (
                  <DatabaseStatusIcon status={status} />
                ) : null}
                <span
                  title={activeDatabase?.displayName ?? ''}
                  className="max-w-[250px] truncate text-foreground font-medium"
                >
                  {activeDatabase?.displayName ?? ''}
                </span>
              </div>
            </div>
            <ChevronsUpDown className="ml-auto" />
          </SidebarMenuButton>
        </PopoverTrigger>
        <PopoverContent
          className="w-max min-w-[max(250px,var(--radix-popper-anchor-width))] max-w-[480px] p-0"
          align="start"
          side="bottom"
          sideOffset={4}
        >
          <Command>
            <CommandInput placeholder="Search databases..." className="h-9" />
            <CommandList>
              <CommandEmpty>No database found</CommandEmpty>
              <CommandGroup>
                {databases.map((database) => (
                  <Link
                    key={database.id}
                    to={`${pathname.replace(databaseName, database.name)}?idCompany=${idCompany}`}
                    onClick={() => setOpen(false)}
                  >
                    <CommandItem
                      value={database.id}
                      keywords={[database.displayName]}
                      className="cursor-pointer"
                    >
                      <div className="flex size-6 items-center justify-center rounded-md border">
                        {activeDatabase?.id === database.id ? (
                          <Check className="size-3.5 shrink-0" />
                        ) : (
                          <Database className="size-3.5 shrink-0" />
                        )}
                      </div>
                      <span
                        className={cn('truncate', {
                          'font-medium': activeDatabase?.id === database?.id,
                        })}
                      >
                        {database.displayName}
                      </span>
                    </CommandItem>
                  </Link>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </BreadcrumbItem>
  )
}
